/**
 * Two-step size choice on the product page (blocks/size-class-picker.liquid).
 * Step 1: Klein / Mittel / Groß buttons. Step 2: the theme's weight buttons (variant picker),
 * limited to the weights of the chosen class. The variant picker re-renders itself after every
 * change, so the filter is re-applied whenever the markup around it changes.
 */

/** Upper weight limit (kg) of "small" and "medium" per product; everything above is "large". */
const SIZE_CLASS_LIMITS = {
  'entrana-skirt-steak': [0.3, 0.6],
  'picanha-tafelspitz': [1.2, 1.6],
  'roastbeef-bife-angosto': [1.4, 1.9],
  'ribeye-ojo-de-bife': [2.6, 3.2],
  'filet-lomo': [1.4, 1.8],
};

class SizeClassPicker extends HTMLElement {
  #variants = [];
  #observer = null;
  #limits = null;
  #sizeClass = null;

  connectedCallback() {
    this.#observer?.disconnect();
    this.#limits = SIZE_CLASS_LIMITS[this.dataset.handle];
    if (!this.#limits) {
      this.hidden = true;
      return;
    }

    try {
      this.#variants = JSON.parse(this.querySelector('[data-size-class-variants]')?.textContent || '[]');
    } catch {
      this.#variants = [];
    }
    this.#variants.forEach((variant) => {
      variant.sizeClass = this.#classify(variant.title);
    });

    this.#classInputs.forEach((input) => {
      input.disabled = !this.#variants.some((v) => v.sizeClass === input.value && v.available);
      input.onchange = () => this.#onClassChange(input.value);
    });

    // The variant picker can be replaced as a whole after a change, so watch its surroundings.
    this.#observer = new MutationObserver(() => this.#sync());
    this.#observer.observe(this.#scope, { childList: true, subtree: true });
    this.#sync();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  get #classInputs() {
    return [...this.querySelectorAll('input[type="radio"]')];
  }

  get #scope() {
    return this.closest('product-details, .product-details, section') || document.body;
  }

  get #weightInputs() {
    const picker = this.#scope.querySelector('variant-picker');
    return picker ? [...picker.querySelectorAll('input[type="radio"][data-variant-id]')] : [];
  }

  /** Reads the weight ("ca. 1.2kg", "approx. 1,2 kg") from a variant title. */
  #classify(title) {
    const match = String(title).match(/(\d+(?:[.,]\d+)?)\s*kg/i);
    if (!match) return null;
    const weight = parseFloat(match[1].replace(',', '.'));
    const [small, medium] = this.#limits;
    if (weight <= small + 0.001) return 'small';
    if (weight <= medium + 0.001) return 'medium';
    return 'large';
  }

  #classOf(variantId) {
    return this.#variants.find((v) => String(v.id) === String(variantId))?.sizeClass || null;
  }

  /** Step 1 changed: jump to the first available weight of that class (if the current one is outside). */
  #onClassChange(sizeClass) {
    this.#sizeClass = sizeClass;
    const inputs = this.#weightInputs;
    const checked = inputs.find((input) => input.checked);
    this.#filter(inputs);
    if (checked && this.#classOf(checked.dataset.variantId) === sizeClass) return;

    const target = inputs.find(
      (input) => this.#classOf(input.dataset.variantId) === sizeClass && input.dataset.optionAvailable !== 'false'
    );
    target?.click();
  }

  /** Keeps step 1 in line with the selected weight and hides weights of other classes in step 2. */
  #sync() {
    const inputs = this.#weightInputs;
    const checked = inputs.find((input) => input.checked);
    const current = checked ? this.#classOf(checked.dataset.variantId) : null;
    if (current) this.#sizeClass = current;

    this.#classInputs.forEach((input) => {
      const isChecked = input.value === this.#sizeClass;
      if (input.checked !== isChecked) input.checked = isChecked;
    });
    this.#filter(inputs);
  }

  #filter(inputs) {
    inputs.forEach((input) => {
      const label = input.closest('label');
      if (!label) return;
      const hide = this.#sizeClass !== null && this.#classOf(input.dataset.variantId) !== this.#sizeClass;
      const display = hide ? 'none' : '';
      if (label.style.display !== display) label.style.display = display;
    });
  }
}

if (!customElements.get('size-class-picker')) {
  customElements.define('size-class-picker', SizeClassPicker);
}
